// src/features/sheets/components/SpreadsheetFormulaBar.tsx
// Formula bar component with autocomplete

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import type { CellPosition } from "../types";
import { colIndexToLetter } from "../utils/formulaEngine";
import { AVAILABLE_METRICS } from "../utils/liveDataService";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type AutocompleteMode = "none" | "ticker" | "metric";

interface AutocompleteSuggestion {
  value: string;
  label: string;
  description?: string;
}

// Popular tokens for autocomplete
const POPULAR_TOKENS: AutocompleteSuggestion[] = [
  { value: "BTC", label: "BTC", description: "Bitcoin" },
  { value: "ETH", label: "ETH", description: "Ethereum" },
  { value: "SOL", label: "SOL", description: "Solana" },
  { value: "BNB", label: "BNB", description: "BNB Chain" },
  { value: "XRP", label: "XRP", description: "Ripple" },
  { value: "ADA", label: "ADA", description: "Cardano" },
  { value: "AVAX", label: "AVAX", description: "Avalanche" },
  { value: "DOGE", label: "DOGE", description: "Dogecoin" },
  { value: "DOT", label: "DOT", description: "Polkadot" },
  { value: "MATIC", label: "MATIC", description: "Polygon" },
  { value: "LINK", label: "LINK", description: "Chainlink" },
  { value: "UNI", label: "UNI", description: "Uniswap" },
  { value: "AAVE", label: "AAVE", description: "Aave" },
  { value: "MKR", label: "MKR", description: "Maker" },
  { value: "CRV", label: "CRV", description: "Curve" },
  { value: "LDO", label: "LDO", description: "Lido" },
  { value: "ARB", label: "ARB", description: "Arbitrum" },
  { value: "OP", label: "OP", description: "Optimism" },
];

const METRIC_SUGGESTIONS: AutocompleteSuggestion[] = AVAILABLE_METRICS.map((m) => ({
  value: m.key,
  label: m.key,
  description: m.description,
}));

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

export interface SpreadsheetFormulaBarProps {
  selectedCell: CellPosition | null;
  editingCell: CellPosition | null;
  editValue: string;
  cellValue: string;
  onEditChange: (value: string) => void;
  onEditFocus: () => void;
  onEditBlur: () => void;
  onEditKeyDown: (e: React.KeyboardEvent) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const SpreadsheetFormulaBar: React.FC<SpreadsheetFormulaBarProps> = ({
  selectedCell,
  editingCell,
  editValue,
  cellValue,
  onEditChange,
  onEditFocus,
  onEditBlur,
  onEditKeyDown,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const isInteractingWithAutocomplete = useRef(false);

  // Autocomplete state
  const [autocompleteMode, setAutocompleteMode] = useState<AutocompleteMode>("none");
  const [autocompleteIndex, setAutocompleteIndex] = useState(0);
  const [autocompleteFilter, setAutocompleteFilter] = useState("");

  // Detect autocomplete mode
  const detectAutocompleteMode = useCallback(
    (value: string): { mode: AutocompleteMode; filter: string } => {
      const upperValue = value.toUpperCase();

      // Check for =LIVE( pattern - need ticker
      const liveOpenMatch = upperValue.match(/=LIVE\s*\(\s*"?([^",)]*)?$/i);
      if (liveOpenMatch) {
        return { mode: "ticker", filter: (liveOpenMatch[1] || "").toUpperCase() };
      }

      // Check for =LIVE("TICKER", pattern - need metric
      const liveMetricMatch = upperValue.match(/=LIVE\s*\(\s*"[^"]+"\s*,\s*"?([^",)]*)?$/i);
      if (liveMetricMatch) {
        return { mode: "metric", filter: (liveMetricMatch[1] || "").toLowerCase() };
      }

      return { mode: "none", filter: "" };
    },
    []
  );

  // Update autocomplete when editValue changes
  useEffect(() => {
    if (editingCell && editValue) {
      const { mode, filter } = detectAutocompleteMode(editValue);
      setAutocompleteMode(mode);
      setAutocompleteFilter(filter);
      setAutocompleteIndex(0);
    } else {
      setAutocompleteMode("none");
      setAutocompleteFilter("");
    }
  }, [editValue, editingCell, detectAutocompleteMode]);

  // Get filtered suggestions
  const autocompleteSuggestions = useMemo((): AutocompleteSuggestion[] => {
    if (autocompleteMode === "ticker") {
      return POPULAR_TOKENS.filter(
        (t) =>
          t.value.includes(autocompleteFilter) ||
          t.description?.toUpperCase().includes(autocompleteFilter)
      );
    }
    if (autocompleteMode === "metric") {
      return METRIC_SUGGESTIONS.filter(
        (m) =>
          m.value.includes(autocompleteFilter) ||
          m.description?.toLowerCase().includes(autocompleteFilter)
      );
    }
    return [];
  }, [autocompleteMode, autocompleteFilter]);

  // Insert autocomplete selection
  const insertAutocompletion = useCallback(
    (suggestion: AutocompleteSuggestion) => {
      if (autocompleteMode === "ticker") {
        const newValue = editValue.replace(
          /=LIVE\s*\(\s*"?[^",)]*$/i,
          `=LIVE("${suggestion.value}", `
        );
        onEditChange(newValue);
      } else if (autocompleteMode === "metric") {
        const newValue = editValue.replace(
          /=LIVE\s*\(\s*("[^"]+")\s*,\s*"?[^",)]*$/i,
          `=LIVE($1, "${suggestion.value}")`
        );
        onEditChange(newValue);
        setTimeout(() => {
          setAutocompleteMode("none");
        }, 0);
      }
      setAutocompleteIndex(0);
    },
    [autocompleteMode, editValue, onEditChange]
  );

  // Handle keyboard in formula bar
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle autocomplete navigation
    if (autocompleteMode !== "none" && autocompleteSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setAutocompleteIndex((prev) =>
          prev < autocompleteSuggestions.length - 1 ? prev + 1 : 0
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setAutocompleteIndex((prev) =>
          prev > 0 ? prev - 1 : autocompleteSuggestions.length - 1
        );
        return;
      }
      if (e.key === "Tab" || e.key === "Enter") {
        e.preventDefault();
        const selected = autocompleteSuggestions[autocompleteIndex];
        if (selected) {
          insertAutocompletion(selected);
        }
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setAutocompleteMode("none");
        return;
      }
    }

    // Pass to parent handler
    onEditKeyDown(e);
  };

  // Handle blur with autocomplete check
  const handleBlur = (e: React.FocusEvent) => {
    if (isInteractingWithAutocomplete.current) {
      return;
    }
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget?.closest(".autocomplete-dropdown")) {
      return;
    }
    setTimeout(() => {
      if (isInteractingWithAutocomplete.current) return;
      onEditBlur();
      setAutocompleteMode("none");
    }, 200);
  };

  return (
    <div className="relative flex items-center gap-2 px-2 py-1 border-b border-slate-700/50 bg-slate-800/30 flex-shrink-0">
      {/* Cell reference */}
      <span className="text-[10px] font-medium text-slate-400 bg-slate-700/50 px-1.5 py-0.5 rounded min-w-[32px] text-center">
        {selectedCell
          ? `${colIndexToLetter(selectedCell.col)}${selectedCell.row + 1}`
          : "—"}
      </span>

      {/* Input */}
      <div className="flex-1 relative">
        <input
          ref={inputRef}
          type="text"
          value={editingCell ? editValue : cellValue}
          onChange={(e) => {
            if (editingCell) {
              onEditChange(e.target.value);
            } else if (selectedCell) {
              onEditFocus();
              onEditChange(e.target.value);
            }
          }}
          onFocus={() => {
            if (selectedCell && !editingCell) {
              onEditFocus();
            }
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Enter value or formula (=)"
          className="w-full bg-transparent text-xs text-slate-200 focus:outline-none font-mono placeholder:text-slate-600"
        />

        {/* Autocomplete Dropdown */}
        {autocompleteMode !== "none" && autocompleteSuggestions.length > 0 && (
          <div
            ref={autocompleteRef}
            className="autocomplete-dropdown absolute left-0 top-full mt-1 z-50 w-64 max-h-48 overflow-y-auto bg-slate-800 border border-slate-600 rounded-md shadow-xl"
            onMouseDown={() => {
              isInteractingWithAutocomplete.current = true;
            }}
            onMouseUp={() => {
              isInteractingWithAutocomplete.current = false;
            }}
          >
            <div className="px-2 py-1 text-[10px] text-slate-500 border-b border-slate-700 bg-slate-800/80 sticky top-0">
              {autocompleteMode === "ticker" ? "Select Token" : "Select Metric"} — ↑↓
              navigate, Tab/Enter select
            </div>
            {autocompleteSuggestions.map((suggestion, index) => (
              <button
                key={suggestion.value}
                type="button"
                ref={(el) => {
                  if (index === autocompleteIndex && el) {
                    el.scrollIntoView({ block: "nearest" });
                  }
                }}
                className={`w-full text-left px-2 py-1.5 text-xs flex items-center justify-between gap-2 ${
                  index === autocompleteIndex
                    ? "bg-blue-600/40 text-white"
                    : "text-slate-300 hover:bg-slate-700/50"
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  isInteractingWithAutocomplete.current = true;
                }}
                onClick={() => {
                  insertAutocompletion(suggestion);
                  inputRef.current?.focus();
                  isInteractingWithAutocomplete.current = false;
                }}
                onMouseEnter={() => setAutocompleteIndex(index)}
              >
                <span className="font-mono font-medium">{suggestion.label}</span>
                {suggestion.description && (
                  <span className="text-slate-500 text-[10px] truncate">
                    {suggestion.description}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SpreadsheetFormulaBar;
